import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, TrendingUp, Mic, BookOpen, Target, ChevronRight, CheckCircle2 } from 'lucide-react';

const REDIRECT_URL = encodeURIComponent(window.location.origin + '/dashboard');
const AUTH_URL = `${process.env.REACT_APP_AUTH_URL}/?redirect=${REDIRECT_URL}`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const LandingPage = () => {
  const [pricing, setPricing] = useState(null);
  const [monetizationEnabled, setMonetizationEnabled] = useState(false);
  const [site, setSite] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/config`)
      .then((r) => r.ok && r.json())
      .then((d) => {
        if (d) {
          setPricing(d.pricing);
          setMonetizationEnabled(Boolean(d.monetization_enabled));
        }
      })
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/config`)
      .then((r) => r.ok && r.json())
      .then((d) => d && setSite(d))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-brand-ink font-body antialiased">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-brand-border">
        <nav className="container mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src="/logo.png" alt="ReflectiveMind" className="h-12 sm:h-14 w-auto" />
          </Link>
          <button
            data-testid="nav-cta-btn"
            onClick={() => (window.location.href = AUTH_URL)}
            className="inline-flex items-center gap-1.5 bg-brand-ink hover:bg-black text-white text-sm font-medium px-4 sm:px-5 py-2 rounded-full transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Sign in / Sign up
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative bg-stripe-glow overflow-hidden">
        <div className="container mx-auto px-5 sm:px-8 lg:px-12 pt-12 sm:pt-20 lg:pt-24 pb-12 sm:pb-20 lg:pb-24">
          <motion.div
            initial="initial"
            animate="animate"
            variants={{ animate: { transition: { staggerChildren: 0.1 } } }}
            className="max-w-3xl"
          >
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-2 px-3 py-1 mb-6 bg-brand-accent-soft text-brand-accent rounded-full text-xs font-medium"
            >
              <Sparkles className="h-3 w-3" />
              AI-powered reflective journaling
            </motion.div>
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-brand-ink"
            >
              Think clearly.
              <br />
              <span className="bg-gradient-to-r from-brand-accent to-[#5EAEFF] bg-clip-text text-transparent">
                Grow on purpose.
              </span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 sm:mt-6 text-base sm:text-lg lg:text-xl text-brand-ink-muted leading-relaxed max-w-2xl"
            >
              Five quiet minutes a day, transformed by AI into the patterns that
              shape your next one. Private, focused, and built for people who think.
            </motion.p>
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:items-center"
            >
              <button
                data-testid="hero-primary-cta"
                onClick={() => (window.location.href = AUTH_URL)}
                className="inline-flex items-center justify-center gap-2 bg-brand-accent hover:bg-brand-accent-hover text-white font-medium px-6 py-3 rounded-full transition-all shadow-[0_4px_14px_rgba(5,112,222,0.35)] hover:shadow-[0_6px_20px_rgba(5,112,222,0.45)] hover:-translate-y-0.5"
              >
                Start reflecting
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-sm text-brand-ink-muted">
                {monetizationEnabled
                  ? 'Free 14-day trial · No card required'
                  : 'Free to use · Sign up in seconds'}
              </p>
            </motion.div>
          </motion.div>

          {/* Animated dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 sm:mt-16 lg:mt-20 max-w-5xl mx-auto"
          >
            <DashboardMockup />
          </motion.div>
        </div>
      </section>

      {/* Logos / trust strip */}
      <section className="border-y border-brand-border bg-white">
        <div className="container mx-auto px-5 sm:px-8 lg:px-12 py-10">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-brand-ink-muted">
            Built for practitioners · founders · makers · students · therapists
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32">
        <div className="max-w-2xl mb-12 sm:mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-semibold mb-4">
            Inside ReflectiveMind
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight tracking-tight text-brand-ink">
            A clean space for the
            <br className="hidden sm:block" />{' '}
            thoughts that matter.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          <FeatureCard
            icon={<BookOpen className="h-5 w-5" />}
            title="Capture"
            body="Type or speak — we transcribe it. Tag, annotate, and revisit later."
          />
          <FeatureCard
            icon={<Sparkles className="h-5 w-5" />}
            title="Reflect"
            body="Weekly, monthly, and yearly AI insights surface patterns you'd never spot alone."
          />
          <FeatureCard
            icon={<TrendingUp className="h-5 w-5" />}
            title="Notice"
            body="A gentle line that tells you whether the season is rising, falling, or steady."
          />
          <FeatureCard
            icon={<Target className="h-5 w-5" />}
            title="Act"
            body="Insights become small, achievable next steps you can edit and follow through."
          />
          <FeatureCard
            icon={<Mic className="h-5 w-5" />}
            title="Voice journaling"
            body="Hold the mic, speak your mind. Private, transcribed, and yours."
          />
          <FeatureCard
            icon={<CheckCircle2 className="h-5 w-5" />}
            title="Quarterly reviews"
            body="A scheduled, AI-guided look-back to course-correct and celebrate."
          />
        </div>
      </section>

      {/* Pricing — only when enabled */}
      {monetizationEnabled && pricing && (
        <section className="bg-brand-bg-soft border-y border-brand-border">
          <div className="container mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
            <div className="text-center max-w-xl mx-auto mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-semibold mb-3">
                Pricing
              </p>
              <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-brand-ink">
                Simple. Transparent.
              </h2>
              <p className="mt-4 text-brand-ink-muted">Cancel anytime, no questions asked.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <PricingCard
                title="Monthly"
                price={`${pricing?.currency_symbol || '£'}${pricing?.monthly?.price}`}
                cadence="/month"
                features={['All features', 'Cancel anytime', '14-day free trial']}
                cta="Start monthly"
                onClick={() => (window.location.href = AUTH_URL)}
              />
              <PricingCard
                title="Annual"
                price={`${pricing?.currency_symbol || '£'}${pricing?.annual?.price}`}
                cadence="/year"
                features={[
                  'Everything in monthly',
                  `Save ${pricing?.annual?.savings_percentage || 33}%`,
                  `Just ${pricing?.currency_symbol || '£'}${(pricing?.annual?.price / 12).toFixed(2)}/mo`,
                ]}
                cta="Start annual"
                onClick={() => (window.location.href = AUTH_URL)}
                highlighted
              />
            </div>
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section className="container mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24 lg:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-ink leading-tight">
            Begin tonight.
            <br />
            <span className="bg-gradient-to-r from-brand-accent to-[#5EAEFF] bg-clip-text text-transparent">
              Look back next month.
            </span>
          </h2>
          <button
            data-testid="footer-cta-btn"
            onClick={() => (window.location.href = AUTH_URL)}
            className="mt-8 sm:mt-10 inline-flex items-center gap-2 bg-brand-accent hover:bg-brand-accent-hover text-white font-medium px-7 py-3.5 rounded-full transition-all shadow-[0_4px_14px_rgba(5,112,222,0.35)] hover:shadow-[0_6px_20px_rgba(5,112,222,0.45)] hover:-translate-y-0.5"
          >
            Start reflecting — free
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-brand-border bg-white">
        <div className="container mx-auto px-5 sm:px-8 lg:px-12 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="ReflectiveMind" className="h-9 sm:h-10 w-auto opacity-80" />
            </div>
            <div className="flex items-center gap-5 text-sm text-brand-ink-muted">
              <Link
                to="/privacy"
                className="hover:text-brand-ink transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="hover:text-brand-ink transition-colors"
                data-testid="footer-terms-link"
              >
                Terms
              </Link>
              <p className="text-xs" data-testid="footer-copyright">
                &copy; {site?.copyright_year || new Date().getFullYear()}{' '}
                {site?.company_name || 'ReflectiveMind'}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ---------------- Reusable bits ---------------- */

const FeatureCard = ({ icon, title, body }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -4 }}
    className="group bg-white border border-brand-border rounded-2xl p-6 sm:p-7 transition-shadow hover:shadow-[0_8px_30px_rgba(0,0,0,0.05)]"
  >
    <div className="inline-flex items-center justify-center w-10 h-10 mb-4 bg-brand-accent-soft text-brand-accent rounded-xl">
      {icon}
    </div>
    <h3 className="font-display text-lg font-semibold text-brand-ink mb-2 tracking-tight">
      {title}
    </h3>
    <p className="text-brand-ink-muted leading-relaxed text-[15px]">{body}</p>
  </motion.div>
);

const PricingCard = ({ title, price, cadence, features, cta, onClick, highlighted = false }) => (
  <div
    className={`rounded-2xl p-7 sm:p-8 border transition-all ${
      highlighted
        ? 'bg-brand-ink text-white border-brand-ink shadow-[0_12px_40px_rgba(0,0,0,0.15)]'
        : 'bg-white border-brand-border'
    }`}
  >
    <p
      className={`text-xs uppercase tracking-[0.2em] font-semibold mb-3 ${
        highlighted ? 'text-[#5EAEFF]' : 'text-brand-accent'
      }`}
    >
      {title}
    </p>
    <div className="flex items-baseline gap-1 mb-5">
      <span className={`font-display text-5xl font-bold tracking-tight ${highlighted ? 'text-white' : 'text-brand-ink'}`}>
        {price}
      </span>
      <span className={`text-sm ${highlighted ? 'text-white/60' : 'text-brand-ink-muted'}`}>
        {cadence}
      </span>
    </div>
    <ul className={`space-y-2 mb-7 text-sm ${highlighted ? 'text-white/85' : 'text-brand-ink-muted'}`}>
      {features.map((f, i) => (
        <li key={i} className="flex items-center gap-2">
          <CheckCircle2 className={`h-4 w-4 ${highlighted ? 'text-[#5EAEFF]' : 'text-brand-accent'}`} />
          {f}
        </li>
      ))}
    </ul>
    <button
      onClick={onClick}
      className={`w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full font-medium text-sm transition-all hover:-translate-y-0.5 ${
        highlighted
          ? 'bg-white text-brand-ink hover:bg-white/90'
          : 'bg-brand-ink text-white hover:bg-black'
      }`}
    >
      {cta}
      <ChevronRight className="h-4 w-4" />
    </button>
  </div>
);

/* ---------------- Animated dashboard mockup ---------------- */

const DashboardMockup = () => {
  const reflections = [
    { time: 'Mon · 9:42 PM', text: "Worried about the launch but pushed through. Felt good after.", mood: '😌' },
    { time: 'Tue · 8:15 PM', text: 'Had real focus today. Three deep work blocks. Need to protect mornings.', mood: '🎯' },
    { time: 'Wed · 7:50 PM', text: 'Bumpy meeting. Need to remember to listen first.', mood: '🤔' },
  ];

  return (
    <div className="relative">
      {/* Browser chrome */}
      <div className="rounded-t-2xl bg-[#F4F5F7] border border-brand-border px-4 py-3 flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-[#FF5F56]" />
        <span className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
        <span className="w-3 h-3 rounded-full bg-[#27C93F]" />
        <div className="ml-4 flex-1 max-w-md mx-auto bg-white rounded-md px-3 py-1 text-xs text-brand-ink-muted text-center">
          reflective-mind.org/dashboard
        </div>
      </div>
      {/* Mockup body */}
      <div className="rounded-b-2xl bg-white border border-t-0 border-brand-border p-5 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Reflections feed */}
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink-muted">
              Recent reflections
            </p>
            <div className="flex items-center gap-1.5 text-xs text-brand-ink-muted">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-success animate-pulse" />
              Live
            </div>
          </div>
          <div className="space-y-3">
            {reflections.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + i * 0.25, duration: 0.5 }}
                className="flex gap-3 p-4 bg-[#FAFAFA] rounded-xl border border-brand-border"
              >
                <div className="text-2xl shrink-0">{r.mood}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-brand-ink-muted mb-1">
                    {r.time}
                  </p>
                  <p className="text-sm text-brand-ink leading-relaxed">{r.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Side: insights + sentiment */}
        <div className="space-y-4">
          {/* Sentiment chart */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="p-4 bg-gradient-to-br from-brand-accent-soft to-white rounded-xl border border-brand-border"
          >
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-brand-ink-muted mb-3">
              Sentiment · 7d
            </p>
            <svg viewBox="0 0 200 60" className="w-full h-12">
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 1.1, duration: 1.4, ease: 'easeOut' }}
                d="M 5 40 Q 30 35 50 30 T 100 22 T 150 18 T 195 12"
                stroke="#0570DE"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
              />
              <motion.circle
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 2.4, type: 'spring' }}
                cx="195"
                cy="12"
                r="3.5"
                fill="#0570DE"
              />
            </svg>
            <p className="text-xs text-brand-ink-muted mt-2">
              <span className="text-brand-success font-semibold">↑ 18%</span> trending upward
            </p>
          </motion.div>

          {/* AI insight bubble */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.5 }}
            className="p-4 bg-brand-ink text-white rounded-xl"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="h-3 w-3 text-[#5EAEFF]" />
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#5EAEFF]">
                AI Insight
              </p>
            </div>
            <p className="text-xs leading-relaxed text-white/90">
              You&apos;re most clear-headed on days you protect mornings. Try blocking{' '}
              <span className="text-[#5EAEFF]">9–11am</span> tomorrow.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Soft glow underneath */}
      <div className="absolute inset-x-12 -bottom-6 h-12 bg-brand-accent/20 blur-3xl -z-10" />
    </div>
  );
};

export default LandingPage;
